<template>
  <div class="cfg-chart-card">
    <div class="cfg-chart-header">
      <div class="cfg-chart-header-start">
        <h3 :title="widget.title">{{ widget.title || 'Table' }}</h3>
        <div v-if="rows.length" class="cfg-table-search-wrap">
          <i class="fa fa-search cfg-table-search-icon" aria-hidden="true"></i>
          <input
            v-model="searchQuery"
            type="search"
            class="cfg-table-search"
            placeholder="Search…"
            aria-label="Search table"
            @click.stop
          />
          <button
            v-if="searchQuery"
            type="button"
            class="cfg-table-search-clear"
            title="Clear search"
            aria-label="Clear search"
            @click.stop="searchQuery = ''"
          >
            <i class="fa fa-times" aria-hidden="true"></i>
          </button>
        </div>
      </div>
      <span class="cfg-row-count">{{ rowCountLabel }}</span>
    </div>
    <div class="cfg-chart-body">
      <div v-if="!rows.length" class="cfg-chart-empty">
        <i class="fa fa-table" aria-hidden="true"></i>
        <span>No data to display</span>
      </div>
      <div v-else class="cfg-table-wrap">
        <table class="cfg-table">
          <thead>
            <tr>
              <th
                v-for="c in displayCols"
                :key="c"
                @click="sortOn(c)"
                class="cfg-th"
              >
                <span>{{ c }}</span>
                <i
                  v-if="sortKey === c"
                  :class="sortDir === 'asc' ? 'fa fa-sort-up' : 'fa fa-sort-down'"
                ></i>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, i) in visibleRows" :key="i">
              <td v-for="c in displayCols" :key="c" :class="cellClass(r[c])">
                {{ formatCell(r[c]) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { groupBy, computeCustomColumn } from './utils/aggregations'

export default {
  name: 'DashboardTable',
  props: {
    widget: { type: Object, required: true },
    data: { type: Array, default: () => [] },
  },
  data() {
    return { sortKey: null, sortDir: 'asc', searchQuery: '' }
  },
  computed: {
    rawRowCount() { return this.data.length },
    rowCountLabel() {
      const total = this.rows.length
      const shown = this.visibleRows.length
      if (this.searchQuery.trim()) return `${shown} of ${total} rows`
      return `${total} rows`
    },
    rows() {
      const cfg = this.widget
      let rows
      const agg = cfg.agg || null
      const groupField = cfg.x
      const valField = cfg.y

      if (groupField && valField && agg) {
        const grouped = groupBy(this.data, groupField, valField, agg)
        rows = Object.entries(grouped).map(([k, v]) => ({
          [groupField]: k,
          [valField]: v,
        }))
        const sortDir = cfg.sort || 'none'
        if (sortDir === 'desc') rows.sort((a, b) => (b[valField] || 0) - (a[valField] || 0))
        else if (sortDir === 'asc') rows.sort((a, b) => (a[valField] || 0) - (b[valField] || 0))
      } else {
        rows = this.data
      }

      const customCols = cfg.custom_columns || []
      if (customCols.length) {
        rows = rows.map(r => {
          const out = { ...r }
          customCols.forEach(cc => {
            out[cc.name] = computeCustomColumn(r, cc.formula)
          })
          return out
        })
      }
      return rows
    },
    displayCols() {
      const cfg = this.widget
      if (Array.isArray(cfg.columns) && cfg.columns.length) return cfg.columns
      if (!this.rows.length) return []
      const customCols = cfg.custom_columns || []
      const baseCols = Object.keys(this.rows[0]).filter(
        k => !customCols.some(cc => cc.name === k)
      )
      return baseCols.concat(customCols.map(cc => cc.name))
    },
    filteredRows() {
      const q = this.searchQuery.trim().toLowerCase()
      if (!q) return this.rows
      const cols = this.displayCols
      return this.rows.filter(r =>
        cols.some(c => String(r[c] ?? '').toLowerCase().includes(q))
      )
    },
    visibleRows() {
      const base = this.filteredRows
      if (!this.sortKey) return base
      const dir = this.sortDir === 'asc' ? 1 : -1
      const sk = this.sortKey
      return [...base].sort((a, b) => {
        const va = a[sk], vb = b[sk]
        const na = Number(va), nb = Number(vb)
        if (isFinite(na) && isFinite(nb)) return (na - nb) * dir
        return String(va || '').localeCompare(String(vb || '')) * dir
      })
    },
  },
  methods: {
    sortOn(c) {
      if (this.sortKey === c) {
        this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = c; this.sortDir = 'asc'
      }
    },
    formatCell(v) {
      if (v == null || v === '') return ''
      if (typeof v === 'number' && !Number.isInteger(v)) {
        const abs = Math.abs(v)
        if (abs >= 1000) return v.toLocaleString('en-IN', { maximumFractionDigits: 2 })
        return v.toFixed(2)
      }
      if (typeof v === 'number') return v.toLocaleString('en-IN')
      return v
    },
    cellClass(v) {
      return (typeof v === 'number' || (typeof v === 'string' && /^-?\d[\d,.]*%?$/.test(v)))
        ? 'cfg-num' : 'cfg-str'
    },
  },
}
</script>

<style scoped>
.cfg-chart-card {
  background: var(--dash-surface); border-radius: 12px;
  border: 1px solid var(--dash-border);
  display: flex; flex-direction: column; height: 100%;
  min-width: 0; overflow: hidden;
  transition: background 0.3s ease;
}
.cfg-chart-header {
  padding: 14px 18px 10px 18px; flex-shrink: 0;
  display: flex; align-items: center; justify-content: space-between; gap: 10px;
}
.cfg-chart-header-start {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  flex: 1 1 auto;
}
.cfg-chart-header h3 {
  margin: 0; font-size: .95rem; font-weight: 600; color: var(--dash-text);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  flex-shrink: 1;
  min-width: 0;
}
.cfg-row-count {
  font-size: .72rem; color: var(--dash-text-muted);
  background: var(--dash-surface2); padding: 2px 8px; border-radius: 10px;
  flex-shrink: 0;
}
.cfg-table-search-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.cfg-table-search-icon {
  color: var(--dash-text-muted);
  font-size: .75rem;
  flex-shrink: 0;
}
.cfg-table-search {
  width: 120px;
  min-width: 120px;
  height: 28px;
  padding: 0 8px;
  border: 1px solid var(--dash-border);
  border-radius: 8px;
  background: var(--dash-input-bg, var(--dash-surface));
  color: var(--dash-text);
  font-size: .82rem;
  box-sizing: border-box;
}
.cfg-table-search:focus {
  outline: none;
  border-color: var(--dash-accent);
  box-shadow: 0 0 0 2px color-mix(in srgb, var(--dash-accent) 20%, transparent);
}
.cfg-table-search::placeholder { color: var(--dash-text-muted); }
.cfg-table-search-clear {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  border-radius: 6px;
  background: var(--dash-surface2);
  color: var(--dash-text-muted);
  cursor: pointer;
}
.cfg-table-search-clear:hover {
  color: var(--dash-text);
  background: var(--dash-border);
}
.cfg-chart-body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 0;
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.cfg-chart-empty {
  position: absolute; inset: 0;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 8px; color: var(--dash-text-muted); font-size: .85rem;
}
.cfg-chart-empty i { font-size: 1.8rem; opacity: .5; }
.cfg-table-wrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
.cfg-table {
  width: 100%; border-collapse: collapse; font-size: .82rem;
}
.cfg-table thead th {
  position: sticky; top: 0; z-index: 1;
  background: var(--dash-surface2); color: var(--dash-text-muted);
  text-align: left; padding: 8px 12px;
  border-bottom: 2px solid var(--dash-border);
  font-weight: 600; cursor: pointer; user-select: none;
  white-space: nowrap;
}
.cfg-table thead th:hover { color: var(--dash-text); }
.cfg-table thead th i { margin-left: 6px; color: var(--dash-accent); }
.cfg-table tbody td {
  padding: 7px 12px;
  border-bottom: 1px solid var(--dash-border);
  color: var(--dash-text);
}
.cfg-table tbody tr:hover td { background: var(--dash-surface2); }
.cfg-table td.cfg-num { text-align: right; font-variant-numeric: tabular-nums; }
.cfg-table td.cfg-str { text-align: left; }
</style>
